import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useAlert } from '../functions/useAlert';
import { NotificationContext } from '../contexts/NotificationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import isBetween from 'dayjs/plugin/isBetween';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Typography, Stack, FormControl,
  InputLabel, Select, MenuItem, TextField, Box, Tooltip,
  ToggleButtonGroup, ToggleButton, Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon, Add as AddIcon } from '@mui/icons-material';
import AutoRecordingErrorModal from './AutoRecordingErrorModal';
import CalendlySchedulingFlow from './CalendlySchedulingFlow';
import ClientContactSelector from './ClientContactSelector';
import ManualSchedulingForm from './ManualSchedulingForm';
import SchedulingMethodSelector from './SchedulingMethodSelector';
import { useSchedulerOAuth } from '../hooks/useSchedulerOAuth';
import { useCalendlyIntegration } from '../hooks/useCalendlyIntegration';
import { useMeetingSchedulerData } from '../hooks/useMeetingSchedulerData';
import { useMeetingSchedulerForm } from '../hooks/useMeetingSchedulerForm';
import type { MeetingType } from '../hooks/useMeetingSchedulerForm';
import {
  createMeeting, postMeetingUpdate, uploadRecording,
  initiateTranscription, sendCalendlyInvite,
} from '../services/meetings.service';
import { toggleAutoRecording } from '../services/connectors.service';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

interface MeetingSchedulerProps {
  open: boolean;
  mode: 'create' | 'edit' | 'upload';
  initialData?: any | null;
  onClose: () => void;
  onSave: () => void;
  clientId?: string;
  advisors: any[];
  login: any;
  meetingTypes: MeetingType[];
  contactsRefreshCounter?: number;
  onUploadSuccess?: (meetingId: string, meetingData: any) => void;
  context?: 'client' | 'global';
  clients?: any[];
  onOpenLinkContact?: (clientId: any) => void;
  onOpenCreateContact?: (clientId?: string) => void;
}

export type { MeetingType };

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
  if (!parts.length) return '';
  return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const MeetingScheduler: React.FC<MeetingSchedulerProps> = ({
  open, mode, initialData, onClose, onSave, clientId, advisors, login,
  meetingTypes, onUploadSuccess, contactsRefreshCounter, context = 'client', clients = [],
}) => {
  const [setAlert] = useAlert();
  const { addNotification, startPollingForTranscript } = useContext(NotificationContext);

  const [isSaving, setIsSaving] = useState(false);
  const [savingIntent, setSavingIntent] = useState<'save' | 'resend' | null>(null);
  const [autoRecordingErrorOpen, setAutoRecordingErrorOpen] = useState(false);
  const [pendingMeetingPayload, setPendingMeetingPayload] = useState<any>(null);
  const [schedulingMethod, setSchedulingMethod] = useState<'default' | 'calendly'>('default');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [advisorSearch, setAdvisorSearch] = useState('');

  const { isMicrosoftConnected, isZoomEnabled, handleConnectMicrosoft } = useSchedulerOAuth(open, login?.data?.id);

  const {
    calendlyEvents, isCalendlyLoading, calendlyError,
    selectedCalendlyUrl, handleCalendlyEventSelect, fetchCalendlyEvents, resetCalendly,
  } = useCalendlyIntegration();

  const { linkedContacts, isContactsLoading, fetchContactsForClient, fetchOrgContacts } =
    useMeetingSchedulerData(login?.data?.orgId);

  const {
    form, setForm, errors, setErrors, filteredMeetingTypes, filteredAdvisors,
    handleChange, handleSelectChange, handleTimeChange, handleDurationChange,
    handleCalendarSelect, handleCalendarNavigate, validateForm, buildMeetingPayload, resetForm,
  } = useMeetingSchedulerForm({
    open, mode, initialData, clientId, login, meetingTypes, advisors, context,
    schedulingMethod, selectedCalendlyUrl, fetchContactsForClient, fetchOrgContacts, resetCalendly,
  });

  // Refresh contacts when parent signals a change or another flow dispatches contactsUpdated
  useEffect(() => {
    if (typeof contactsRefreshCounter === 'undefined') return;
    if (form.selectedClient) fetchContactsForClient(form.selectedClient);
    else fetchOrgContacts();
  }, [contactsRefreshCounter, form.selectedClient, fetchContactsForClient, fetchOrgContacts]);

  useEffect(() => {
    const handler = () => {
      if (form.selectedClient) fetchContactsForClient(form.selectedClient);
      else fetchOrgContacts();
    };
    window.addEventListener('contactsUpdated', handler as EventListener);
    return () => window.removeEventListener('contactsUpdated', handler as EventListener);
  }, [form.selectedClient, fetchContactsForClient, fetchOrgContacts]);

  const selectedContactsObjects = useMemo(
    () => linkedContacts.filter((c) => form.selectedContacts.includes(c._id)),
    [linkedContacts, form.selectedContacts],
  );
  const selectedAdvisorsObjects = useMemo(
    () => filteredAdvisors.filter((a) => form.selectedAdvisors.includes(a._id)),
    [filteredAdvisors, form.selectedAdvisors],
  );

  const handleClose = () => { resetForm(); onClose(); };

  const handleUpload = async (meetingId: string) => {
    if (!uploadFile) return;
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('orgId', login?.data?.orgId || '');
    fd.append('userId', login?.data?.id || '');
    fd.append('clientId', form.selectedClient || clientId!);
    fd.append('isManualUpload', 'true');

    const res: any = await uploadRecording(meetingId, fd);
    if (res.hasError) throw new Error(res.message || 'File upload failed.');

    addNotification({ id: res.data._id, meetingId, status: 'loading', title: 'Processing transcript...', message: 'This may take a moment.', createdAt: new Date().toISOString() });

    const attendees: { Advisor: string[]; Client: string[] } = { Advisor: [], Client: [] };
    form.selectedAdvisors.forEach((id: string) => {
      const adv = advisors.find((a) => a._id === id);
      if (adv) attendees.Advisor.push(`${adv.firstName || ''} ${adv.lastName || ''}`.trim());
    });
    form.selectedContacts.forEach((id: string) => {
      const cont = linkedContacts.find((c) => c._id === id);
      if (cont) attendees.Client.push(cont.name);
    });

    try {
      const tRes: any = await initiateTranscription({ ...res.data, attendees, meetingId });
      if (tRes?.data?._id) startPollingForTranscript({ recordingId: res.data._id, meetingId, transcriptionId: tRes.data._id });
    } catch { /* ignore */ }
  };

  const handleSubmit = async (resendInvite = false) => {
    if (!validateForm()) return setAlert('Please fill all required fields.', 'error');
    if (mode !== 'edit' && schedulingMethod === 'default' && form.meetingMedium === 'Zoom' && !isZoomEnabled)
      return setAlert('You need to connect your Zoom account first.', 'error');
    if (mode === 'edit' && !form.start.isBefore(form.end))
      return setAlert('End time must be after start time.', 'warning');

    setIsSaving(true);
    setSavingIntent(resendInvite ? 'resend' : 'save');
    try {
      if (schedulingMethod === 'calendly') {
        const res: any = await sendCalendlyInvite({
          calendlyUrl: selectedCalendlyUrl, clients: form.selectedContacts,
          meetingType: form.meetingType, title: form.title,
          advisors: form.selectedAdvisors, targetClientId: form.selectedClient || clientId,
        });
        if (!res.success) throw new Error(res.message || 'Failed to send invite');
        setAlert('Invitation sent successfully!', 'success');
        onSave(); handleClose(); return;
      }

      const payload: any = buildMeetingPayload();
      if (mode === 'edit') payload.data.resendInvite = resendInvite;
      let response: any;
      let meetingId = '';

      if (mode === 'edit') {
        response = await postMeetingUpdate(initialData._id, payload);
        meetingId = initialData._id;
      } else {
        response = await createMeeting(payload);
        if (response.errorCode === 'AUTO_RECORDING_SETUP_FAILED') {
          setPendingMeetingPayload(payload);
          setAutoRecordingErrorOpen(true);
          return;
        }
        meetingId = response.data?._id;
      }

      if (response.error || !response.success) throw new Error(response.message || 'An error occurred.');
      if (mode === 'upload' && uploadFile && meetingId) await handleUpload(meetingId);

      setAlert(mode === 'edit' ? 'Meeting updated!' : 'Meeting scheduled!', 'success');
      if (onUploadSuccess && meetingId) onUploadSuccess(meetingId, response.data);
      onSave(); handleClose();
    } catch (error: any) {
      setAlert(error.message || 'Failed to save meeting.', 'error');
    } finally {
      setIsSaving(false);
      setSavingIntent(null);
    }
  };

  const handleTurnOffAutoRecordingAndRetry = async () => {
    if (!pendingMeetingPayload) return;
    setIsSaving(true);
    try {
      await toggleAutoRecording({ orgId: login.data.orgId });
      const res: any = await createMeeting(pendingMeetingPayload);
      if (res.error || !res.success) throw new Error(res.message || 'An error occurred.');
      setAutoRecordingErrorOpen(false);
      setPendingMeetingPayload(null);
      setAlert('Meeting scheduled! (Auto-recording has been disabled)', 'success');
      if (onUploadSuccess && res.data?._id) onUploadSuccess(res.data._id, res.data);
      onSave(); handleClose();
    } catch (error: any) {
      setAlert(error.message || 'Failed to create meeting.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const dialogTitle = mode === 'edit' ? 'Edit Meeting' : mode === 'upload' ? 'Upload Meeting Manually' : 'Schedule Meeting';

  // Edit mode always offers both actions as two plain buttons: "Save" saves only,
  // "Resend Invite" saves and re-sends the calendar invite. Neither is gated on
  // what changed — whether the attendees need a fresh invite is the adviser's call.
  const canResendInvite = mode === 'edit';

  const submitLabel = schedulingMethod === 'calendly' ? 'Send Invitation'
    : mode === 'edit' ? 'Save'
    : mode === 'upload' ? 'Create Meeting' : 'Schedule Meeting';
  const isSubmitDisabled = (
    (form.meetingCategory === 'financial' && form.selectedContacts.length === 0) ||
    (context === 'global' && !form.selectedClient)
  ) || isSaving;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth PaperProps={{ sx: { borderRadius: '12px', maxWidth: '900px', width: '100%' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.25rem', fontWeight: 700 }}>
          {dialogTitle}
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {mode === 'upload' && (
              <Box>
                <input accept="audio/*,video/*,.vtt,.json" type="file" style={{ display: 'none' }} id="file-upload-input"
                  onChange={(e) => { const f = e.target.files?.[0] ?? null; setUploadFile(f); setUploadFileName(f?.name ?? ''); }} />
                <label htmlFor="file-upload-input">
                  <Button variant="outlined" color="primary" component="span" startIcon={<CloudUploadIcon />}>Upload File (Optional)</Button>
                </label>
                {uploadFileName && <Typography sx={{ mt: 1, ml: 2 }}><strong>File:</strong> {uploadFileName}</Typography>}
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Meeting type
              </Typography>
              <ToggleButtonGroup
                value={form.meetingCategory}
                exclusive
                onChange={(_, newVal) => {
                  if (newVal) setForm((prev: any) => ({ ...prev, meetingCategory: newVal }));
                }}
                size="small"
                fullWidth
                sx={{ '& .MuiToggleButton-root': { flex: 1, textTransform: 'none', fontWeight: 600 } }}
              >
                {/* Display label only — the stored category value stays
                    "financial", which the backend and every other consumer read. */}
                <ToggleButton value="financial">Advice</ToggleButton>
                <ToggleButton value="general">General</ToggleButton>
              </ToggleButtonGroup>
              {mode === 'edit' && form.meetingCategory !== ((initialData?.category as 'financial' | 'general') ?? 'financial') && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5, fontSize: '0.8rem' }}>
                  Changing category will hide the existing file note or AI summary for this meeting. The content is preserved and will return if you switch back.
                </Alert>
              )}
            </Box>

            <TextField name="title" label="Title" value={form.title} onChange={handleChange} fullWidth error={errors.title} />

            {(mode === 'create' || (mode !== 'edit' && schedulingMethod !== 'calendly')) && (
              <SchedulingMethodSelector
                mode={mode}
                schedulingMethod={schedulingMethod}
                meetingMedium={form.meetingMedium}
                isZoomEnabled={isZoomEnabled}
                isMicrosoftConnected={isMicrosoftConnected}
                onSchedulingMethodChange={(method) => {
                  setSchedulingMethod(method);
                  if (method === 'calendly' && calendlyEvents.length === 0 && !calendlyError) fetchCalendlyEvents();
                }}
                onMeetingMediumChange={(medium) => setForm((prev: any) => ({ ...prev, meetingMedium: medium }))}
                onConnectMicrosoft={handleConnectMicrosoft}
              />
            )}

            <ClientContactSelector
              context={context} mode={mode} clients={clients}
              linkedContacts={linkedContacts} isContactsLoading={isContactsLoading}
              filteredAdvisors={filteredAdvisors}
              selectedContactsObjects={selectedContactsObjects}
              selectedAdvisorsObjects={selectedAdvisorsObjects}
              selectedClient={form.selectedClient}
              contactSearch={contactSearch} advisorSearch={advisorSearch} clientSearch={clientSearch}
              errors={{ selectedClient: errors.selectedClient, selectedContacts: errors.selectedContacts }}
              onClientChange={async (id) => {
                const contacts = await fetchContactsForClient(id);
                const primary = contacts.find((c) => c.isPrimary) || contacts[0] || null;
                setForm((prev: any) => ({ ...prev, selectedClient: id, selectedContacts: primary ? [primary._id] : [] }));
                setErrors((prev: any) => ({ ...prev, selectedClient: false }));
              }}
              onContactsChange={(ids) => {
                setForm((prev: any) => ({ ...prev, selectedContacts: ids }));
                if (ids.length > 0) setErrors((prev: any) => ({ ...prev, selectedContacts: false }));
              }}
              onAdvisorsChange={(ids) => setForm((prev: any) => ({ ...prev, selectedAdvisors: ids }))}
              onSearchChange={(field, value) => {
                if (field === 'clientSearch') setClientSearch(value);
                else if (field === 'contactSearch') setContactSearch(value);
                else setAdvisorSearch(value);
              }}
              onClose={handleClose}
              onContactsRefresh={fetchContactsForClient}
              getInitials={getInitials}
            />

            <TextField name="description" label="Description (Optional)" value={form.description} onChange={handleChange} fullWidth multiline rows={2} />

            {form.meetingCategory === 'financial' && (
              <FormControl fullWidth size="small" error={errors.meetingType}>
                <InputLabel>File note template</InputLabel>
                <Select name="meetingTypeId" value={form.meetingTypeId} onChange={handleSelectChange} label="File note template" size="small">
                  {filteredMeetingTypes.map((t) => <MenuItem key={t._id} value={t._id}>{t.templateName}</MenuItem>)}
                  {['global admin', 'orgadmin'].includes(login?.data?.role) && (
                    <MenuItem value="new-template" sx={{ color: 'primary.main', fontWeight: 'bold', borderTop: '1px solid #e0e0e0', mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AddIcon fontSize="small" /> New template</Box>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}

            {schedulingMethod === 'calendly' ? (
              <CalendlySchedulingFlow
                calendlyEvents={calendlyEvents} isCalendlyLoading={isCalendlyLoading}
                calendlyError={calendlyError} selectedCalendlyUrl={selectedCalendlyUrl}
                onCalendlyEventSelect={handleCalendlyEventSelect} hasError={errors.calendlyEvent}
              />
            ) : (
              <ManualSchedulingForm
                start={form.start} end={form.end} duration={form.duration} mode={mode}
                onStartChange={(val) => handleTimeChange('start', val)}
                onDurationChange={handleDurationChange}
                onCalendarSelect={handleCalendarSelect}
                onCalendarNavigate={handleCalendarNavigate}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          {canResendInvite && (
            <Tooltip title={isSubmitDisabled && !isSaving ? 'Please fill all required fields' : ''}>
              <span style={{ flex: 1 }}>
                <LoadingButton
                  onClick={() => void handleSubmit(true)}
                  variant="contained"
                  loading={isSaving && savingIntent === 'resend'}
                  disabled={isSubmitDisabled}
                  fullWidth
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Resend Invite
                </LoadingButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isSubmitDisabled && !isSaving ? 'Please fill all required fields' : ''}>
            <span style={{ flex: 1 }}>
              <LoadingButton
                onClick={() => void handleSubmit(false)}
                variant="contained"
                loading={isSaving && savingIntent === 'save'}
                disabled={isSubmitDisabled}
                fullWidth
              >
                {submitLabel}
              </LoadingButton>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <AutoRecordingErrorModal
        open={autoRecordingErrorOpen}
        onClose={() => { setAutoRecordingErrorOpen(false); setPendingMeetingPayload(null); }}
        onTurnOffAndRetry={handleTurnOffAutoRecordingAndRetry}
        loading={isSaving}
      />
    </>
  );
};

export default MeetingScheduler;
